import { CheckCircleIcon, XCircleIcon } from "@heroicons/react/24/solid";
import dayjs from "dayjs";
import { FC, useEffect, useState } from "react";
import { BoardingOutcomeCode, IBoardingResult } from "../../interfaces/endpoints/IBoarding.types";
import Button from "../ui/Button";
import { firstScanText } from "./firstScanText";

export const UNDO_WINDOW_MS = 30000;
const NAMES_SHOWN = 3;

const REASONS: Record<BoardingOutcomeCode, string> = {
  boarded: "",
  valid: "",
  already_used: "Bileta është përdorur",
  cancelled: "Bileta është anuluar",
  not_paid: "Bileta nuk është paguar",
  wrong_date: "Bileta është për një datë tjetër",
  wrong_route: "Bileta është për një linjë tjetër",
  other_operator: "Bileta është e një operatori tjetër",
  unknown_ticket: "Bileta nuk u gjet"
};

export const allowsBoarding = (code: BoardingOutcomeCode) => code === "boarded" || code === "valid";

const formatTime = (value?: string | null) => (value ? dayjs(value).format("HH:mm") : "");
const formatDateTime = (value?: string | null) => (value ? dayjs(value).format("DD.MM.YYYY HH:mm") : "");

const names = (passengers: { firstName: string; lastName: string }[]) => {
  const shown = passengers.slice(0, NAMES_SHOWN).map((p) => `${p.firstName} ${p.lastName}`.trim());
  const rest = passengers.length - shown.length;
  return rest > 0 ? `${shown.join(", ")} +${rest}` : shown.join(", ");
};

type Props = {
  result: IBoardingResult;
  shownAt: number;
  onUndo?: (attemptId: number) => void;
  undoing?: boolean;
};

const ResultCard: FC<Props> = ({ result, shownAt, onUndo, undoing }) => {
  const ok = allowsBoarding(result.outcomeCode);
  const ticket = result.ticket;
  const undoAttemptId = ok && onUndo ? result.attemptId : null;
  const offersUndo = !!undoAttemptId;
  const [canUndo, setCanUndo] = useState(() => offersUndo && Date.now() - shownAt < UNDO_WINDOW_MS);

  useEffect(() => {
    if (!canUndo) return;
    const timer = setTimeout(() => setCanUndo(false), Math.max(0, UNDO_WINDOW_MS - (Date.now() - shownAt)));
    return () => clearTimeout(timer);
  }, [canUndo, shownAt]);

  const movedBus = !!ticket?.boardedTripId && ticket.boardedTripId !== ticket.bookedTripId;
  const bookedElsewhere = movedBus ? formatTime(ticket?.bookedDeparture) : "";

  return (
    <div
      data-outcome={result.outcomeCode}
      className={`rounded-lg p-4 text-white shadow-lg ${ok ? "bg-green-700" : "bg-red-700"}`}
    >
      <div className="flex items-start gap-3">
        {ok ? (
          <CheckCircleIcon aria-hidden className="h-10 w-10 shrink-0" />
        ) : (
          <XCircleIcon aria-hidden className="h-10 w-10 shrink-0" />
        )}
        <div className="min-w-0 flex-1">
          <p className="text-4xl font-bold leading-tight">{ok ? "E vlefshme" : "E pavlefshme"}</p>
          {!ok && <p className="text-xl font-semibold">{REASONS[result.outcomeCode]}</p>}
          {result.outcomeCode === "already_used" && result.firstBoarding && (
            <p>{firstScanText(result.firstBoarding, shownAt)}</p>
          )}
        </div>
        {ok && ticket && (
          <div className="text-right">
            <p className="text-5xl font-extrabold leading-none">{ticket.passengerCount}</p>
            <span className="block text-xs">{ticket.passengerCount === 1 ? "udhëtar" : "udhëtarë"}</span>
          </div>
        )}
      </div>

      {ticket && (
        <div className="mt-3 space-y-1">
          <p className="font-semibold">
            {ticket.from} → {ticket.to}
          </p>
          {result.outcomeCode === "wrong_date" ? (
            <p>{formatDateTime(ticket.bookedDeparture)}</p>
          ) : (
            <p>
              {formatTime(ticket.boardedDeparture ?? ticket.bookedDeparture)}
              {bookedElsewhere && (
                <span className="ml-2 rounded bg-white/20 px-1.5">Rezervuar për {bookedElsewhere}</span>
              )}
            </p>
          )}
          {ticket.passengers.length > 0 && <p className="break-words">{names(ticket.passengers)}</p>}
          <div className="flex justify-between text-xs">
            <span>#{ticket.bookingReference}</span>
            <span>{dayjs(shownAt).format("HH:mm:ss")}</span>
          </div>
        </div>
      )}

      {offersUndo && (
        <Button
          btnType="custom"
          padding="py-3"
          disabled={undoing || !canUndo}
          onClick={() => undoAttemptId && onUndo?.(undoAttemptId)}
          className={`mt-3 w-full border border-white/70 text-white ${canUndo ? "" : "invisible"}`}
        >
          Anulo skanimin
        </Button>
      )}
    </div>
  );
};

export default ResultCard;
